import { Injectable } from "@angular/core";
import { SiteAppCommand } from "@fabric-types/app-site";
import { PlatformServiceCommand } from "@fabric-types/svc-platform";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { GeolocationService, JurisdictionSelector } from "@obg/ngx.core";
import { DocumentRef, PlatformService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, of } from "rxjs";
import { catchError, filter, map, switchMap, take, tap, throttleTime, withLatestFrom } from "rxjs/operators";

import { CommonState } from "../state";

import { AwsLocationService } from "./aws-location/aws-location.service";
import { CurrentLocationModel } from "./current-location/current-location.model";
import { CurrentLocationService } from "./current-location/current-location.service";
import { GeoFencingAction } from "./geofencing.action";
import { GeofencingConfigService } from "./geofencing.config";
import {
	ARGENTINA_JURISDICTIONS,
	POSITION_ERROR_MAP,
	MAP_RADAR_GEO_FRAUD_ERROR,
	GEOFENCING_ERROR,
	ARGENTINA_COUNTRY_CODE,
	ALLOWED_ARGENTINIAN_LOCATION_IDS,
} from "./geofencing.const";

@Injectable()
export class GeofencingEffect {
	checkLocation$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(GeoFencingAction.validateLocation),
				switchMap(() =>
					this.geolocationService.getPosition$().pipe(
						map((position) => {
							if (this.config.isArgentinaGeofencingEnabled()) {
								return GeoFencingAction.getArgentinaLocationSuccess(position);
							}

							if (this.config.isAwsLocationServiceEnabled()) {
								return GeoFencingAction.getAwsLocationSuccess(position);
							}

							return GeoFencingAction.validateLocationFailure(GEOFENCING_ERROR.GeofencingDisabled);
						}),
						catchError((error) =>
							of(GeoFencingAction.validateLocationFailure(POSITION_ERROR_MAP[error.code]))
						)
					)
				)
			)
		)
	);

	getArgentinaLocationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GeoFencingAction.getArgentinaLocationSuccess),
			map((x) => x.position.coords),
			withLatestFrom(this.store.pipe(select(JurisdictionSelector.get), ignoreNil())),
			filter(([, jurisdiction]) => ARGENTINA_JURISDICTIONS.includes(jurisdiction)),
			switchMap(([{ latitude, longitude }]) =>
				this.currentLocationService.getLocationId(latitude, longitude).pipe(
					map((id) => this.validateArgentineLocation(id)),
					catchError(() => of(GeoFencingAction.validateLocationFailure(GEOFENCING_ERROR.ServerError)))
				)
			)
		)
	);

	getBrazilLocationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GeoFencingAction.getBrazilLocationSuccess),
			switchMap(({ isFraud, error }) => of(GeoFencingAction.validateLocationSuccess(!isFraud, error)))
		)
	);

	getAwsLocationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GeoFencingAction.getAwsLocationSuccess),
			switchMap((x) =>
				this.awsLocationService
					.getGpsLocationAdministrativeArea(x.position.coords.latitude, x.position.coords.longitude)
					.pipe(
						map(
							(administrativeArea) =>
								!!administrativeArea &&
								administrativeArea !== this.config.getAwsBlockedAdministrativeArea()
						),
						map((isValid) => GeoFencingAction.validateLocationSuccess(isValid)),
						catchError(() => of(GeoFencingAction.validateLocationFailure(GEOFENCING_ERROR.ServerError)))
					)
			)
		)
	);

	getAwsIpLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GeoFencingAction.getAwsIpLocation),
			switchMap(() =>
				this.awsLocationService.getIpLocationCountry().pipe(
					map((ipLocation) => !!ipLocation && ipLocation.country === this.config.getIpBlockedCountry()),
					map((isValid) => GeoFencingAction.validateIpLocationSuccess(isValid)),
					catchError(() => of(GeoFencingAction.validateIpLocationFailure(GEOFENCING_ERROR.ServerError)))
				)
			)
		)
	);

	validateBrazilLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GeoFencingAction.validateBrazilLocation),
			filter(() => this.geolocationService.isBrazilGeofencingEnabled()),
			switchMap(() =>
				combineLatest([
					this.geolocationService.getPosition$(),
					this.geolocationService.checkIfTheUserIsFraud$(),
				]).pipe(
					filter(([_, geoFraudDetails]) => !!geoFraudDetails),
					take(1),
					map(([_, geoFraudDetails]) => {
						let reason;
						if (geoFraudDetails.isFraud) {
							reason = geoFraudDetails.reason
								? MAP_RADAR_GEO_FRAUD_ERROR[geoFraudDetails.reason]
								: undefined;
						}
						// Temporary event needed for Brazil until RADAR and geo-fencing/location logic is moved into the appropriate fabric service.
						// When migrated this should be moved to geolocation-flow.prompts.ts
						this.docRef.obgEventBus.dispatch(SiteAppCommand.ValidateLocationSuccess, {
							isFraud: geoFraudDetails.isFraud,
							error: reason,
						});

						return GeoFencingAction.getBrazilLocationSuccess(geoFraudDetails.isFraud, reason);
					}),
					catchError((error) => {
						// Temporary event needed for Brazil until RADAR and geo-fencing/location logic is moved into the appropriate fabric service.
						// When migrated this should be moved to geolocation-flow.prompts.ts
						this.docRef.obgEventBus.dispatch(SiteAppCommand.ValidateLocationFailure, { error });

						return of(
							GeoFencingAction.validateLocationFailure(
								typeof error.code === "number" ? POSITION_ERROR_MAP[error.code] : error
							)
						);
					})
				)
			)
		)
	);

	showErrorAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(GeoFencingAction.showErrorAlert),
				throttleTime(500),
				tap(({ jurisdiction }) => {
					this.docRef.obgEventBus?.dispatch(PlatformServiceCommand.GeolocationShowGeofencingErrorAlert, {
						jurisdiction: jurisdiction as string,
					});
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private store: Store<CommonState>,
		private currentLocationService: CurrentLocationService,
		private geolocationService: GeolocationService,
		private platformService: PlatformService,
		private config: GeofencingConfigService,
		private awsLocationService: AwsLocationService,
		private docRef: DocumentRef
	) {}

	private validateArgentineLocation(response?: CurrentLocationModel) {
		const validLocationIds = this.config.getArgentinaValidLocationIds();

		if (!response || !validLocationIds) {
			return GeoFencingAction.validateLocationSuccess(false);
		}

		const { gpsLocation, ipLocation } = response;
		const isValidIpCountry = ipLocation?.CountryCode === ARGENTINA_COUNTRY_CODE;
		const isAnyArgentinianLocationId = ALLOWED_ARGENTINIAN_LOCATION_IDS.includes(gpsLocation?.AdministrativeArea);
		const isValidArgentineGpsLocation = validLocationIds.gpsLocation.includes(gpsLocation?.AdministrativeArea);
		const isValidArgentineIpLocation = validLocationIds.ipLocation.includes(ipLocation?.AdministrativeArea);
		const isUsingVpnOrProxy = response.ipInfo?.AnonymousIP || response.ipInfo?.HostingProviderIP;

		if (isUsingVpnOrProxy && this.config.isArgentinaVpnDetectionEnabled()) {
			return GeoFencingAction.validateArgentineLocationSuccess(
				false,
				gpsLocation?.AdministrativeArea,
				GEOFENCING_ERROR.VpnOrProxyDetected
			);
		}

		if (!isAnyArgentinianLocationId || (!isValidIpCountry && this.config.isArgentinaIpLocationEnabled())) {
			return GeoFencingAction.validateLocationSuccess(false);
		}

		if (
			!isValidArgentineGpsLocation &&
			(!isValidArgentineIpLocation || !this.config.isArgentinaIpLocationEnabled())
		) {
			return GeoFencingAction.validateArgentineLocationSuccess(
				false,
				gpsLocation?.AdministrativeArea,
				GEOFENCING_ERROR.InvalidArgentinePosition
			);
		}

		if (
			isValidArgentineGpsLocation &&
			((isValidArgentineIpLocation && isValidIpCountry) || !this.config.isArgentinaIpLocationEnabled())
		) {
			return GeoFencingAction.validateArgentineLocationSuccess(true, gpsLocation?.AdministrativeArea, undefined);
		}

		return GeoFencingAction.validateLocationSuccess(false);
	}
}
